import { parseFilterModel } from '@/lib/createAgGridDatasource';
import type { SortOrder } from '@/types';

/** The subset of AG Grid's `ColumnState` that `buildDownloadOptions` reads. */
export interface SortColumnState {
  colId: string;
  sort?: 'asc' | 'desc' | null;
  sortIndex?: number | null;
}

export interface DownloadOptions<TSortField extends string, TFilters> {
  sortBy?: TSortField;
  sortOrder?: SortOrder;
  filters?: TFilters | null;
}

/**
 * Pure translation of a grid's live sort/filter state into a CSV export's query options. Shared
 * by the studies/samples/instrument-run download builders so this logic lives in one place;
 * each caller supplies its own valid sort fields and filter-key whitelist (a page's grid can
 * expose columns the download endpoint's contract doesn't support).
 */
export function buildDownloadOptions<TSortField extends string, TFilters extends Record<string, unknown>>(
  columnState: readonly SortColumnState[],
  filterModel: Parameters<typeof parseFilterModel>[0],
  validSortFields: readonly TSortField[],
  validFilterFields: readonly (keyof TFilters & string)[],
  booleanFilterFields?: ReadonlySet<string>,
): DownloadOptions<TSortField, TFilters> {
  // Sort by sortIndex, not column order, so a multi-sort's primary column (not just the leftmost
  // sorted one) drives the export.
  const sortedColumn = columnState
    .filter(col => col.sort)
    .toSorted((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))[0];
  const isValidSortField = (colId: string | undefined): colId is TSortField =>
    (validSortFields as readonly string[]).includes(colId ?? '');
  const sortBy = isValidSortField(sortedColumn?.colId) ? sortedColumn.colId : undefined;

  const parsedFilters = parseFilterModel(filterModel, booleanFilterFields);
  const filterFieldSet: ReadonlySet<string> = new Set(validFilterFields);
  const filters = Object.fromEntries(Object.entries(parsedFilters).filter(([key]) => filterFieldSet.has(key)));

  return {
    sortBy,
    // Only set alongside a valid sortBy — otherwise the download endpoint applies this direction
    // to its own default sort field instead of honoring that default outright.
    sortOrder: sortBy ? (sortedColumn?.sort ?? undefined) : undefined,
    filters: Object.keys(filters).length > 0 ? (filters as TFilters) : null,
  };
}
